import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { CameraPreview } from "@capacitor-community/camera-preview";
import { Loader2, AlertCircle, X, Camera, ImagePlus, Check, ChevronDown, ChevronUp, Layers, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import { CardImage } from "./CardImage";
import { scanRecognize, fetchCard } from "../lib/api";
import { packAvailable, getLocalEngine, getRemoteFilesEngine, localManifest } from "../lib/scanner/pack";
import { computeVariants, isCustomVariant } from "../lib/variants";
import { ballForVariant } from "../lib/balls";
import { rarityInfo } from "../lib/rarity";
import { LABELS } from "../config/variants";
import { getCollectionKey } from "../lib/collection";

// Scanner de cartes par comparaison d'images (sans service externe) :
// la photo est envoyée au serveur (/scan/recognize) quand le réseau est là,
// sinon reconnue sur le téléphone avec le « pack scanner » hors ligne
// (lib/scanner/, à télécharger dans Réglages). Une photo peut contenir
// plusieurs cartes ; les résultats s'accumulent d'une photo à l'autre (mode
// rafale) jusqu'à « Tout ajouter ».

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.88;
// Préférence (Réglages > Scanner) : reconnaître sur le téléphone même en ligne.
export const SCAN_LOCAL_PREF = "bulbizarbi_scan_local";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image illisible."));
    img.src = src;
  });
}

// Photo réduite (plus grand côté 1 600 px) : c'est ce que le serveur garde de
// toute façon, inutile d'envoyer 10 Mo.
async function shrink(dataUrl) {
  const img = await loadImage(dataUrl);
  let { width, height } = img;
  const k = Math.min(1, MAX_DIMENSION / Math.max(width, height));
  width = Math.round(width * k); height = Math.round(height * k);
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  const url = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  return { url, base64: url.split(",")[1], img: await loadImage(url) };
}

// --- Détection de « tenue stable » pour le déclenchement automatique ---
const SAMPLE_INTERVAL_MS = 450;
const SAMPLE_GRID = 20;
const STABLE_SAMPLES_REQUIRED = 3; // ~1,3 s immobile avant capture
const STABILITY_DIFF_THRESHOLD = 8;

function downsampleGrayscale(base64Jpeg) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = SAMPLE_GRID; canvas.height = SAMPLE_GRID;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, SAMPLE_GRID, SAMPLE_GRID);
      const { data } = ctx.getImageData(0, 0, SAMPLE_GRID, SAMPLE_GRID);
      const gray = new Uint8ClampedArray(SAMPLE_GRID * SAMPLE_GRID);
      for (let i = 0; i < gray.length; i++) { const o = i * 4; gray[i] = (data[o] + data[o + 1] + data[o + 2]) / 3; }
      resolve(gray);
    };
    img.onerror = () => reject(new Error("Échantillon illisible."));
    img.src = `data:image/jpeg;base64,${base64Jpeg}`;
  });
}

function meanAbsDiff(a, b) {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i]);
  return sum / a.length;
}

const isOnline = () => typeof navigator === "undefined" || navigator.onLine !== false;
const preferLocal = () => { try { return localStorage.getItem(SCAN_LOCAL_PREF) === "1"; } catch (e) { return false; } };

// Détails d'une carte candidate (image, rareté, variantes) : déjà fournis par
// le serveur, à compléter via fetchCard (qui a son repli hors ligne) quand la
// reconnaissance a eu lieu sur le téléphone.
const detailCache = new Map();
function useCardDetails(cand) {
  const complete = cand && (cand.image !== undefined || cand.variants !== undefined);
  const [details, setDetails] = useState(() => (complete ? cand : detailCache.get(cand?.id) || null));
  useEffect(() => {
    if (!cand) return;
    if (complete) { setDetails(cand); return; }
    if (detailCache.has(cand.id)) { setDetails(detailCache.get(cand.id)); return; }
    let alive = true;
    fetchCard(cand.id)
      .then((c) => { const d = { ...cand, ...c }; detailCache.set(cand.id, d); if (alive) setDetails(d); })
      .catch(() => { if (alive) setDetails(cand); });
    return () => { alive = false; };
  }, [cand, complete]);
  return details || cand;
}

const CONFIDENCE = {
  high: { label: "Sûr", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  medium: { label: "Probable", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  low: { label: "Incertain", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

const Thumb = ({ card, className }) =>
  card?.image ? <CardImage card={card} className={className} /> : <div className={`${className} bg-zinc-800`} />;

// Une proposition « Ce n'est pas ça » (même illustration ou autre carte).
const AltChoice = ({ cand, onPick }) => {
  const card = useCardDetails(cand);
  return (
    <button onClick={onPick} className="text-left bg-zinc-800/80 hover:bg-zinc-700 rounded-lg p-1.5 transition-colors min-w-0">
      <Thumb card={card} className="w-full aspect-[5/7] object-contain rounded mb-1" />
      <p className="text-[11px] text-white font-medium truncate">{card.name}</p>
      <p className="text-[10px] text-zinc-400 truncate">{card.set_name}{card.localId ? ` · n° ${card.localId}` : ""}</p>
    </button>
  );
};

const ScanEntry = ({ entry, index, collection, onChange, onAdd, onOpenCard }) => {
  const cand = entry.candidates[entry.chosen];
  const card = useCardDetails(cand);
  const variants = card.variants || card.variants_override ? computeVariants(card) : [];
  const variant = entry.variant || variants[0]?.type || "normal";
  const rarity = rarityInfo(card.rarity);
  const ownedVariants = variants.filter((v) => collection[getCollectionKey(card.id, v.type)] === "owned");
  const others = entry.candidates.filter((_, i) => i !== entry.chosen);
  const sameArt = others.filter((c) => c.same_art);
  const rest = others.filter((c) => !c.same_art).slice(0, 12);
  const conf = entry.chosen === entry.initial ? CONFIDENCE[entry.confidence] : null;
  const pick = (c) => onChange({ chosen: entry.candidates.indexOf(c), variant: null, expanded: false, confirmed: true, status: entry.status === "added" ? "pending" : entry.status });

  if (entry.status === "ignored") {
    return (
      <div className="flex items-center justify-between rounded-xl border border-zinc-800 px-3 py-2 text-sm text-zinc-500">
        <span>Carte {index} ignorée</span>
        <button className="text-sky-400 hover:text-sky-300" onClick={() => onChange({ status: "pending" })}>Rétablir</button>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-3 ${entry.status === "added" ? "border-emerald-600/50 bg-emerald-950/20" : "border-zinc-800 bg-zinc-900"}`}>
      <div className="flex gap-3">
        <button className="relative shrink-0" onClick={() => onOpenCard(card.id)} title="Ouvrir la fiche">
          <Thumb card={card} className="w-24 aspect-[5/7] object-contain rounded-md" />
          <span className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center shadow">{index}</span>
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <button className="text-left min-w-0" onClick={() => onOpenCard(card.id)}>
              <p className="text-white font-semibold leading-tight">{card.name}</p>
              <p className="text-xs text-zinc-400 mt-0.5">
                {card.set_name}{card.localId ? ` · n° ${card.localId}` : ""}
              </p>
              {card.rarity && <p className="text-[11px] mt-0.5" style={{ color: rarity.color }}>{card.rarity}</p>}
            </button>
            {conf && <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full border ${conf.cls}`}>{conf.label}</span>}
          </div>

          {variants.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {variants.map((v) => {
                const isCustom = isCustomVariant(v.type);
                const ball = ballForVariant(v.type, isCustom ? v.type : undefined, rarity.tier, card.rarity);
                const owned = collection[getCollectionKey(card.id, v.type)] === "owned";
                const label = isCustom ? v.label : (LABELS[v.type] || v.type);
                return (
                  <button key={v.key} title={`${label}${owned ? " (déjà possédée)" : ""}`}
                    onClick={() => onChange({ variant: v.type, confirmed: true })}
                    className={`flex items-center gap-1 rounded-full pl-0.5 pr-2 py-0.5 text-[11px] border transition-colors ${variant === v.type ? "border-sky-400 bg-sky-500/15 text-white" : "border-zinc-700 text-zinc-400"}`}>
                    <img src={ball.src} alt="" className="w-5 h-5" draggable="false" />
                    {label}{owned && <Check className="w-3 h-3 text-emerald-400" />}
                  </button>
                );
              })}
            </div>
          )}
          {sameArt.length > 0 && !entry.expanded && (
            <div className="mt-2 rounded-lg bg-amber-500/10 border border-amber-500/30 px-2 py-1.5">
              <p className="text-[11px] text-amber-200 flex items-center gap-1">
                <Layers className="w-3.5 h-3.5 shrink-0" />
                Même illustration dans {sameArt.length > 1 ? `${sameArt.length} autres éditions` : "une autre édition"} : vérifie le symbole et le numéro.
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {sameArt.map((c) => (
                  <button key={c.id} onClick={() => pick(c)}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                    {c.set_name}{c.localId ? ` · n° ${c.localId}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
          {entry.confidence === "low" && !entry.confirmed && entry.status === "pending" && (
            <p className="text-[11px] text-red-300 mt-1">Pas sûr de moi : vérifie avec « Ce n'est pas ça », ou ajoute-la si c'est bien elle.</p>
          )}
          {ownedVariants.length > 0 && entry.status !== "added" && (
            <p className="text-[11px] text-emerald-400 mt-1">Déjà dans ta collection : {ownedVariants.map((v) => LABELS[v.type] || v.label).join(", ")}</p>
          )}

          <div className="flex flex-wrap gap-2 mt-2.5">
            {entry.status === "added" ? (
              <span className="inline-flex items-center gap-1 text-sm text-emerald-400"><Check className="w-4 h-4" />Ajoutée ({LABELS[entry.addedVariant] || entry.addedVariant})</span>
            ) : (
              <Button size="sm" className="bg-sky-600 hover:bg-sky-500 text-white h-8" onClick={() => onAdd(card.id, variant)}>
                Ajouter
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-8 text-zinc-300 hover:text-white" onClick={() => onChange({ expanded: !entry.expanded })}>
              Ce n'est pas ça {entry.expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>
            {entry.status !== "added" && (
              <Button size="sm" variant="ghost" className="h-8 text-zinc-500 hover:text-zinc-300" onClick={() => onChange({ status: "ignored" })}>
                <EyeOff className="w-4 h-4 mr-1" />Ignorer
              </Button>
            )}
          </div>
        </div>
      </div>

      {entry.expanded && (
        <div className="mt-3 space-y-3">
          {sameArt.length > 0 && (
            <div>
              <p className="text-xs text-zinc-400 mb-1.5 flex items-center gap-1"><Layers className="w-3.5 h-3.5" />Même illustration, autres éditions</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {sameArt.map((c) => <AltChoice key={c.id} cand={c} onPick={() => pick(c)} />)}
              </div>
            </div>
          )}
          <div>
            <p className="text-xs text-zinc-400 mb-1.5">Autres cartes proches</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {rest.map((c) => <AltChoice key={c.id} cand={c} onPick={() => pick(c)} />)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Photo avec les cartes détectées entourées et numérotées.
const ShotPreview = ({ shot, firstIndex }) => (
  <div className="relative rounded-xl overflow-hidden bg-black mx-auto" style={{ maxWidth: 360 }}>
    <img src={shot.url} alt="" className="w-full block" />
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1 1" preserveAspectRatio="none">
      {shot.cards.map((c, i) => (
        <polygon key={i} points={c.quad.map((p) => p.join(",")).join(" ")} fill="rgba(14,165,233,0.12)"
          stroke={shot.fallback ? "#fbbf24" : "#38bdf8"} strokeWidth="0.008" vectorEffect="non-scaling-stroke"
          style={{ strokeWidth: 2 }} />
      ))}
    </svg>
    {shot.cards.map((c, i) => {
      const x = Math.min(...c.quad.map((p) => p[0])), y = Math.min(...c.quad.map((p) => p[1]));
      return (
        <span key={i} className="absolute w-6 h-6 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center shadow"
          style={{ left: `calc(${x * 100}% - 4px)`, top: `calc(${y * 100}% - 4px)` }}>{firstIndex + i}</span>
      );
    })}
  </div>
);

export const ScannerCapture = ({ open, suspended, onClose, onOpenCard, collection = {}, onSetStatus }) => {
  const native = Capacitor.isNativePlatform();
  // camera = aperçu en direct (appli) | pick = choix d'une photo (site)
  // working = reconnaissance | results = liste des cartes | error
  const [state, setState] = useState(native ? "camera" : "pick");
  const [status, setStatus] = useState("Cadre une ou plusieurs cartes…");
  const [error, setError] = useState(null);
  const [workStep, setWorkStep] = useState(null);
  const [locked, setLocked] = useState(false);
  const [shots, setShots] = useState([]);     // [{id, url, fallback, cards:[{quad}] , entryIds}]
  const [entries, setEntries] = useState([]); // une par carte reconnue, toutes photos confondues

  const activeRef = useRef(false);
  const prevSampleRef = useRef(null);
  const stableCountRef = useRef(0);
  const needMotionRef = useRef(false);
  const loopTimerRef = useRef(null);
  const fileRef = useRef(null);

  const stopLoop = () => { if (loopTimerRef.current) clearTimeout(loopTimerRef.current); loopTimerRef.current = null; };

  const sampleLoop = async () => {
    if (!activeRef.current) return;
    try {
      const { value } = await CameraPreview.captureSample({ quality: 40 });
      if (!activeRef.current) return;
      const sample = await downsampleGrayscale(value);
      if (!activeRef.current) return;
      if (prevSampleRef.current) {
        const diff = meanAbsDiff(sample, prevSampleRef.current);
        if (needMotionRef.current) {
          // Après une capture en rafale : attendre qu'on change de carte
          // avant de redéclencher, sinon on rescannerait la même photo.
          if (diff > STABILITY_DIFF_THRESHOLD * 2) { needMotionRef.current = false; setStatus("Cadre la carte suivante…"); }
        } else if (diff < STABILITY_DIFF_THRESHOLD) {
          stableCountRef.current += 1;
          setLocked(stableCountRef.current >= 2);
          setStatus(stableCountRef.current >= 2 ? "Capture…" : "Ne bouge plus…");
        } else {
          stableCountRef.current = 0; setLocked(false);
          setStatus("Cadre une ou plusieurs cartes…");
        }
      }
      prevSampleRef.current = sample;
      if (stableCountRef.current >= STABLE_SAMPLES_REQUIRED) { await capture(); return; }
    } catch (e) { /* échantillon raté : on retente au prochain tick */ }
    if (activeRef.current) loopTimerRef.current = setTimeout(sampleLoop, SAMPLE_INTERVAL_MS);
  };

  const startCamera = async (afterShot = false) => {
    setError(null); setLocked(false);
    prevSampleRef.current = null; stableCountRef.current = 0; needMotionRef.current = afterShot;
    if (!native) { setState("pick"); return; }
    setState("camera");
    setStatus(afterShot ? "Change de carte…" : "Cadre une ou plusieurs cartes…");
    try {
      await CameraPreview.start({ position: "rear", toBack: true, enableZoom: false, disableAudio: true });
      activeRef.current = true;
      loopTimerRef.current = setTimeout(sampleLoop, SAMPLE_INTERVAL_MS);
    } catch (e) {
      setError("Impossible d'accéder à la caméra. Vérifie l'autorisation dans les paramètres du téléphone.");
      setState("error");
    }
  };

  const stopCamera = async () => {
    activeRef.current = false; stopLoop();
    try { await CameraPreview.stop(); } catch (e) { /* déjà arrêtée */ }
  };

  const recognize = async (photo) => {
    const local = preferLocal() || !isOnline();
    if (!local) {
      try {
        return await scanRecognize(photo.base64);
      } catch (e) {
        if (e?.response) throw new Error(e.response.data?.detail || `Erreur du serveur (${e.response.status})`);
        // pas de réponse : réseau coupé -> moteur du téléphone
      }
    }
    let engine = null;
    if (packAvailable()) {
      if (await localManifest()) engine = await getLocalEngine(setWorkStep);
    } else if (preferLocal()) {
      engine = await getRemoteFilesEngine(setWorkStep); // essai du moteur embarqué depuis le site
    }
    if (!engine) {
      throw new Error(native
        ? "Pas de réseau, et le scanner hors ligne n'est pas encore téléchargé (Réglages > Scanner)."
        : "Serveur injoignable.");
    }
    setWorkStep("analyse");
    return engine.recognize(photo.img);
  };

  const process = async (photo) => {
    setState("working"); setWorkStep(null);
    try {
      const res = await recognize(photo);
      const shotId = Date.now();
      const newEntries = res.cards.map((c, i) => ({
        id: `${shotId}-${i}`, shotId, candidates: c.candidates, chosen: 0, initial: 0,
        confidence: c.confidence, variant: null, status: "pending", expanded: false,
      }));
      if (!newEntries.length) {
        setError("Aucune carte reconnue sur cette photo. Essaie plus près, sans reflet.");
        setState("error");
        return;
      }
      setShots((s) => [{ id: shotId, url: photo.url, fallback: res.fallback, cards: res.cards.map((c) => ({ quad: c.quad })), local: !!res.local }, ...s]);
      setEntries((e) => [...newEntries, ...e]);
      setState("results");
      // Une seule carte sur la photo et reconnue sans doute : on ouvre sa
      // fiche directement. En la fermant, on retombe sur le résultat du scan
      // (« Ce n'est pas ça » reste là si la proposition est fausse).
      if (newEntries.length === 1 && newEntries[0].confidence !== "low") onOpenCard(newEntries[0].candidates[0].id);
    } catch (e) {
      setError(e?.message || String(e));
      setState("error");
    }
  };

  const capture = async () => {
    activeRef.current = false; stopLoop();
    try {
      const { value } = await CameraPreview.capture({ quality: 88 });
      await CameraPreview.stop();
      await process(await shrink(`data:image/jpeg;base64,${value}`));
    } catch (e) {
      try { await CameraPreview.stop(); } catch (_) { /* déjà arrêtée */ }
      setError(e?.message || String(e)); setState("error");
    }
  };

  const onFile = (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try { await process(await shrink(reader.result)); } catch (e) { setError(e.message); setState("error"); }
    };
    reader.readAsDataURL(f);
  };

  useEffect(() => {
    if (open) startCamera();
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fiche ouverte par-dessus : la caméra s'arrête, on reprend au retour.
  useEffect(() => {
    if (!open) return;
    if (suspended && state === "camera") { stopCamera(); setState(entries.length ? "results" : "pick"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suspended]);

  useEffect(() => {
    const active = open && !suspended && state === "camera";
    document.body.classList.toggle("camera-preview-active", active);
    return () => document.body.classList.remove("camera-preview-active");
  }, [open, suspended, state]);

  const handleClose = () => { stopCamera(); onClose(); };
  const updateEntry = (id, patch) => setEntries((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const addEntry = async (entry, cardId, variant) => {
    await onSetStatus(cardId, variant, "owned");
    updateEntry(entry.id, { status: "added", addedVariant: variant, variant });
  };
  // « Tout ajouter » laisse de côté les cartes incertaines que l'utilisateur
  // n'a pas confirmées (photo d'un objet qui n'est pas une carte, mauvais cadrage…).
  const pending = entries.filter((e) => e.status === "pending" && (e.confidence !== "low" || e.confirmed));
  const addAll = async () => {
    for (const e of pending) {
      const cand = e.candidates[e.chosen];
      const card = detailCache.get(cand.id) || cand;
      const vs = card.variants || card.variants_override ? computeVariants(card) : [];
      await addEntry(e, cand.id, e.variant || vs[0]?.type || "normal");
    }
  };
  const again = () => startCamera(entries.length > 0);

  if (!open || suspended) return null;

  if (state === "camera") {
    return createPortal(
      <div className="camera-preview-overlay">
        <div className="camera-preview-topbar">
          <span className="text-white text-sm font-medium">Scanner des cartes</span>
          <button onClick={handleClose} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="camera-preview-guide-wrap">
          <div className={`camera-preview-frame ${locked ? "locked" : ""}`}>
            <span /><span /><span /><span />
          </div>
        </div>
        <div className="camera-preview-bottombar">
          <p className="text-white text-sm mb-3">{status}</p>
          <div className="flex items-center gap-3">
            <Button onClick={() => { stableCountRef.current = STABLE_SAMPLES_REQUIRED; capture(); }} className="bg-sky-600 hover:bg-sky-500 text-white">
              <Camera className="w-4 h-4 mr-2" />Capturer
            </Button>
            {entries.length > 0 && (
              <Button variant="ghost" className="text-white bg-black/40 hover:bg-black/60" onClick={() => { stopCamera(); setState("results"); }}>
                Résultats ({entries.length})
              </Button>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  let shotStart = entries.length + 1;
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-zinc-950 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
        <span className="text-white font-semibold">
          Scanner{entries.length ? ` · ${entries.length} carte${entries.length > 1 ? "s" : ""}` : ""}
        </span>
        <button onClick={handleClose} className="text-zinc-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {state === "pick" && (
          <div className="flex flex-col items-center text-center gap-3 py-10">
            <ImagePlus className="w-10 h-10 text-sky-400" />
            <p className="text-zinc-300 text-sm max-w-xs">Prends en photo une ou plusieurs cartes posées à plat, bien éclairées.</p>
            <Button className="bg-sky-600 hover:bg-sky-500 text-white" onClick={() => fileRef.current?.click()}>
              <Camera className="w-4 h-4 mr-2" />Choisir une photo
            </Button>
          </div>
        )}

        {state === "working" && (
          <div className="flex flex-col items-center gap-3 py-16 text-zinc-400">
            <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
            <p className="text-sm">
              {workStep === "index" ? "Chargement de l'index des cartes…"
                : ["det", "dino", "clip"].includes(workStep) ? "Préparation du scanner hors ligne…"
                : "Reconnaissance en cours…"}
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
            <p className="text-sm text-zinc-300 max-w-xs">{error}</p>
            <div className="flex gap-2 mt-2">
              <Button onClick={() => (native ? startCamera(false) : fileRef.current?.click())} className="bg-sky-600 hover:bg-sky-500 text-white">Réessayer</Button>
              {entries.length > 0 && <Button variant="ghost" className="text-zinc-300" onClick={() => setState("results")}>Résultats</Button>}
            </div>
          </div>
        )}

        {(state === "results" || state === "pick" || state === "error") && shots.map((shot) => {
          const list = entries.filter((e) => e.shotId === shot.id);
          shotStart -= list.length;
          const first = shotStart;
          return (
            <section key={shot.id} className="space-y-3">
              <ShotPreview shot={shot} firstIndex={first} />
              {shot.fallback && (
                <p className="text-xs text-amber-300 text-center">Carte non détectée automatiquement : seul le centre de la photo a été analysé.</p>
              )}
              {shot.local && <p className="text-[11px] text-zinc-500 text-center">Reconnu sur le téléphone (hors ligne).</p>}
              {list.map((e, i) => (
                <ScanEntry key={e.id} entry={e} index={first + i} collection={collection}
                  onChange={(patch) => updateEntry(e.id, patch)}
                  onAdd={(cardId, variant) => addEntry(e, cardId, variant)}
                  onOpenCard={onOpenCard} />
              ))}
            </section>
          );
        })}
      </div>

      {state !== "working" && (
        <div className="border-t border-zinc-800 px-4 py-3 flex gap-2" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
          <Button className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white" onClick={() => (native ? again() : fileRef.current?.click())}>
            <Camera className="w-4 h-4 mr-2" />{entries.length ? "Carte suivante" : "Photo"}
          </Button>
          {pending.length > 0 && (
            <Button className="flex-1 bg-sky-600 hover:bg-sky-500 text-white" onClick={addAll}>
              <Check className="w-4 h-4 mr-2" />Tout ajouter ({pending.length})
            </Button>
          )}
        </div>
      )}
    </div>,
    document.body
  );
};
